import { InfoBlock } from "../InfoBlock";
import { ActionError, type DeliverySectionContentProps } from "./shared";
import "./payments.css";

// F171 (Tomas Pohl 2026-10-06): "Napojení plateb". The TEAM sets up the
// client's payments; the client reads what we need from them (access to their
// Stripe) and where the work stands. No client button: the state is the
// partner's checklist, derived on the server (shared/payments-setup.ts).

export type PaymentsStepState = "locked" | "next" | "working" | "done";

export function paymentsStepStateOf(props: Pick<DeliverySectionContentProps, "salesSteps">): PaymentsStepState {
  return props.salesSteps.find((step) => step.key === "payments")?.state ?? "locked";
}

const PILL: Record<PaymentsStepState, { className: string; key: string }> = {
  locked: { className: "pill-team", key: "payments.pill.locked" },
  next: { className: "pill-client", key: "payments.pill.next" },
  working: { className: "pill-team", key: "payments.pill.working" },
  done: { className: "pill-done", key: "payments.pill.done" }
};

const WORK_ITEMS = ["1", "2", "3", "4", "5"] as const;

export function PaymentsSection(props: DeliverySectionContentProps) {
  const state = paymentsStepStateOf(props);
  const partnerEmail = props.partner?.publicEmail?.trim() || null;
  const calendlyUrl = props.partner?.calendlyUrl?.trim() || null;
  const pill = PILL[state];
  return (
    <section className="m5b-view" aria-labelledby="payments-title" data-payments-state={state}>
      <header className="m5b-heading">
        <p className="eyebrow blue-eyebrow">{props.t("payments.eyebrow")}</p>
        <h1 id="payments-title">{props.t("rail.steps.payments")}</h1>
        <p className="lead">{props.t("payments.copy")}</p>
      </header>

      <InfoBlock
        name="payments-status"
        eyebrow={props.t("payments.statusEyebrow")}
        title={props.t("payments.statusTitle")}
        body={<p>{props.t(`payments.status.${state}`)}</p>}
        meta={<span className={`status-pill ${pill.className}`}>{props.t(pill.key)}</span>}
      />

      {/* Only while the ball is with the client: once the partner ticked
          `access` (working) the request would ask for something already given. */}
      {(state === "next" || state === "locked") && (
        <InfoBlock
          name="payments-access"
          eyebrow={props.t("payments.accessEyebrow")}
          title={props.t("payments.accessTitle")}
          body={(
            <ol className="payments-access-options">
              <li>
                {partnerEmail
                  ? props.t("payments.accessInvite").replace("{email}", partnerEmail)
                  : props.t("payments.accessInviteNoEmail")}
              </li>
              <li>{props.t("payments.accessCall")}</li>
            </ol>
          )}
        >
          {calendlyUrl && (
            <a className="secondary-button compact-button" href={calendlyUrl} target="_blank" rel="noreferrer">
              {props.t("payments.bookCall")}
            </a>
          )}
        </InfoBlock>
      )}

      <InfoBlock
        name="payments-work"
        eyebrow={props.t("payments.workEyebrow")}
        title={props.t("payments.workTitle")}
        body={(
          <ul className="payments-work-list">
            {WORK_ITEMS.map((item) => <li key={item}>{props.t(`payments.work.${item}`)}</li>)}
          </ul>
        )}
      />
      <ActionError t={props.t} issue={props.actionError} />
    </section>
  );
}
